import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth-context';
import { reportsAPI, dashboardAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  FileText,
  Download,
  Calendar,
  TrendingUp,
  ArrowUpRight,
  ArrowDownLeft,
} from 'lucide-react';
import { format, subDays } from 'date-fns';

const COLORS = ['#4338ca', '#10b981', '#f97316', '#ef4444'];

const ReportsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });
  const [chartData, setChartData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [commissions, setCommissions] = useState([]);

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [chartRes, txnRes, commRes] = await Promise.all([
        dashboardAPI.getChartData(30),
        reportsAPI.getTransactions(dateRange.start, dateRange.end),
        reportsAPI.getCommissions(dateRange.start, dateRange.end),
      ]);
      setChartData(chartRes.data);
      setTransactions(txnRes.data);
      setCommissions(commRes.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalCredits = transactions.filter(t => t.type === 'credit').reduce((sum, t) => sum + t.amount, 0);
  const totalDebits = transactions.filter(t => t.type === 'debit').reduce((sum, t) => sum + t.amount, 0);
  const totalCommission = commissions.reduce((sum, c) => sum + c.amount, 0);

  const pieData = [
    { name: 'Credits', value: totalCredits },
    { name: 'Debits', value: totalDebits },
  ];

  const exportToCSV = (data, filename) => {
    if (data.length === 0) return;
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).join(','));
    const csv = [headers, ...rows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const handleExportPdf = (reportType) => {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${baseUrl}/api/reports/export-pdf?report_type=${reportType}&start_date=${dateRange.start}&end_date=${dateRange.end}&token=${token}`;
    window.open(url, '_blank');
  };

  return (
    <DashboardLayout>
      <div className="space-y-5 animate-fade-in" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
            <p className="text-sm text-slate-500 mt-0.5">View and export detailed reports</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Input
                id="start-date" type="date" value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-36 h-9 rounded-lg text-sm" data-testid="start-date-input"
              />
              <span className="text-xs text-slate-400">to</span>
              <Input
                id="end-date" type="date" value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-36 h-9 rounded-lg text-sm" data-testid="end-date-input"
              />
            </div>
            <Button
              variant="outline" size="sm"
              onClick={() => handleExportPdf('recharges')}
              className="rounded-lg h-9 text-xs font-semibold"
              data-testid="export-pdf-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Download PDF
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { title: 'Total Credits', value: `₹${totalCredits.toLocaleString()}`, icon: ArrowDownLeft, color: 'bg-emerald-500', accent: 'green' },
            { title: 'Total Debits', value: `₹${totalDebits.toLocaleString()}`, icon: ArrowUpRight, color: 'bg-red-500', accent: 'orange' },
            { title: 'Commission', value: `₹${totalCommission.toFixed(2)}`, icon: TrendingUp, color: 'bg-[#4680ff]', accent: 'blue' },
            { title: 'Transactions', value: transactions.length.toString(), icon: FileText, color: 'bg-violet-500', accent: 'purple' },
          ].map((s, i) => (
            <div key={i} className={`stat-accent-line ${s.accent}`}>
              <Card className="border border-slate-100 shadow-sm rounded-xl">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em] mb-2">{s.title}</p>
                      <p className="text-xl font-bold text-slate-900 font-mono">{s.value}</p>
                    </div>
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${s.color}`}>
                      <s.icon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="commissions" data-testid="tab-commissions">Commissions</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Line Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Transaction Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <Skeleton className="w-full h-64" />
                  ) : chartData.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                          <XAxis
                            dataKey="date"
                            tickFormatter={(value) => format(new Date(value), 'dd MMM')}
                            tick={{ fontSize: 12 }}
                            stroke="#94a3b8"
                          />
                          <YAxis
                            tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                            tick={{ fontSize: 12 }}
                            stroke="#94a3b8"
                          />
                          <Tooltip
                            formatter={(value) => [`₹${value.toLocaleString()}`, 'Volume']}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="volume"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      No data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Credit vs Debit</CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <Skeleton className="w-full h-64" />
                  ) : (totalCredits > 0 || totalDebits > 0) ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) => `₹${value.toLocaleString()}`}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="flex justify-center gap-6 mt-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-blue-500" />
                          <span className="text-sm">Credits</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500" />
                          <span className="text-sm">Debits</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      No data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Transaction Report</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(transactions, 'transactions')}
                  disabled={transactions.length === 0}
                  data-testid="export-transactions-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No transactions in selected period
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Balance After</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.slice(0, 20).map((txn) => (
                          <TableRow key={txn.id}>
                            <TableCell>
                              <Badge variant={txn.type === 'credit' ? 'default' : 'secondary'}>
                                {txn.type}
                              </Badge>
                            </TableCell>
                            <TableCell className={txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}>
                              {txn.type === 'credit' ? '+' : '-'}₹{txn.amount.toLocaleString()}
                            </TableCell>
                            <TableCell className="max-w-xs truncate">{txn.description}</TableCell>
                            <TableCell>₹{txn.balance_after.toLocaleString()}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {format(new Date(txn.created_at), 'dd MMM yyyy')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Commission Report</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(commissions, 'commissions')}
                  disabled={commissions.length === 0}
                  data-testid="export-commissions-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : commissions.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No commissions in selected period
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Recharge ID</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {commissions.slice(0, 20).map((comm) => (
                          <TableRow key={comm.id}>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">{comm.type}</Badge>
                            </TableCell>
                            <TableCell className="text-green-600 font-medium">
                              +₹{comm.amount.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <code className="text-xs bg-muted px-2 py-1 rounded">
                                {comm.recharge_id.slice(0, 8)}...
                              </code>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {format(new Date(comm.created_at), 'dd MMM yyyy')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
